import { LinkedinRequests } from "domain/entities/linkedinrequests";
import { LinkedinRequestsInputFilters } from "domain/models/linkedinrequests-input-filter";
import Spinner from "presentation/components/Shared/Spinner";
import { useDeleteLinkedinRequestsMutation, useLinkedinRequestsByFilter } from "presentation/hooks/use-query-linkedinrequests";
import { useLinkedinRequestsSelectedStore } from "presentation/stores/use-linkedinrequests-selected-store";
import { useFilterLinkedinRequestsStore } from "presentation/stores/use-filter-linkedinrequests-store";
import { useFilterLinkedinCardsStore } from "presentation/stores/use-filter-linkedincards-store";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useEffect, useRef, useState } from "react";
import HeaderLinkedinRequestsGrid from "./HeaderLinkedinRequestsGrid";
import { Dialog } from "primereact/dialog";
import LinkedinRequestsForm from "../LinkedinRequestsForm";
import TypeLinkedinRequestsDropdown from "./TypeLinkedinRequestsDropdown";
import { Calendar } from "primereact/calendar";
import { formatDateSafe } from "presentation/utils/date-formatter";
import { Tooltip } from "primereact/tooltip";
import linkedInCardsRepository from "infra/repositories/linkedincards-repository";
import { AxiosError } from "axios";

const LinkedinRequestsGrid: React.FC = () => {
  const { filters, setFilters } = useFilterLinkedinRequestsStore();
  const cardsFilters = useFilterLinkedinCardsStore();
  const { selectedRequest, setSelectedRequest } = useLinkedinRequestsSelectedStore();
  const toast = useRef<Toast>(null);
  const [formMode, setFormMode] = useState<"create" | "edit" | "view">("create");
  const [showModal, setShowModal] = useState(false);
  const deleteMutation = useDeleteLinkedinRequestsMutation();
  const [lazyParams, setLazyParams] = useState({
    sortBy: "createdAt",
    sortOrder: "desc",
    limit: 50,
    page: 1,
    filters: {},
  });
  const {
    data: linkedinrequest,
    isLoading,
    isError,
    error,
    refetch,
  } = useLinkedinRequestsByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as LinkedinRequestsInputFilters);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      page: 1,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

   const showToastError = (detail: string) => {
        toast.current?.show({ severity: "error", summary: "Erro", detail, life: 1500 });
    };

    const handleDelete = async(id: string) => {
    try {
      if (!id) throw new Error("ID inválido");
      await deleteMutation.mutateAsync(id);
      // ponytail: só sabemos que o filtro ficou órfão comparando o id apagado
      if (cardsFilters.filters.idRequests?.value === id) {
        cardsFilters.setFilters({
          ...cardsFilters.filters,
          idRequests: { ...cardsFilters.filters.idRequests, value: "" },
        });
      }
      refetch();
    } catch (error: any) {
      showToastError(error?.message || "Erro ao apagar pesquisa.");
    }
  }

  const confirmAction = (message: string, acceptCallback: () => void) => {
        confirmDialog({
          message: message,
          header: "Confirmação",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Sim",
          rejectLabel: "Não",
          accept: acceptCallback,
        });
  };

  // Chamada direta ao repositório, não hook: hooks só rodam no topo do
  // componente, não dentro de onClick. Backend retorna 400 "not found by
  // filter" quando 0 cards, por isso tratado como contagem 0, não erro.
  const takeTotalLinkedinCardsByRequest = async (requestId: string): Promise<number> => {
    try {
      const result = await linkedInCardsRepository.getAll({
        idRequests: requestId,
        limit: 1,
        page: 1,
      });
      return result.pagination?.count ?? 0;
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 400) {
        return 0;
      }
      showToastError("Erro ao verificar vagas relacionadas.");
      return 0;
    }
  }

  const showActionOptions = (rowData: LinkedinRequests) => {
    return (
      <div className="spacing-icons">
        <i
            className="pi pi-times cursor-pointer"
            data-pr-tooltip="Apagar pesquisa"
            onClick={async () => {
              const totalCards = await takeTotalLinkedinCardsByRequest(rowData.id);
              const message =
                totalCards > 0
                  ? `Deseja apagar esta pesquisa?\r\n Vai apagar um total de ${totalCards} vaga(s) relacionada(s).`
                  : `Deseja apagar esta pesquisa?\r\n Ainda não existe nenhuma vaga associada a esta pesquisa.`;
              confirmAction(message, () => handleDelete(rowData.id));
            }}
        />
        <i
          className="pi pi-eye cursor-pointer"
          data-pr-tooltip="Ver detalhes da pesquisa"
          onClick={() => {
            setSelectedRequest(rowData);
            setFormMode("view");
            setShowModal(true)
          }}
        />
        <i
          className="pi pi-pencil cursor-pointer"
          data-pr-tooltip="Editar pesquisa"
          onClick={() => {
            setSelectedRequest(rowData);
            setFormMode("edit");
            setShowModal(true)
          }}
        />
      </div>
    );
  };

  const dialogHeader =
    formMode === "edit"
      ? "Editar Pesquisa"
      : "Ver Pesquisa";

  const dialogWidth = "95vw";
  const dialogHeight = "auto";

  const tooltipStyle = {
    root: { style: { boxShadow: "none", border: "none", transition: "none", animation: "none" } },
    text: { style: { whiteSpace: "nowrap" as const } },
    arrow: { style: { display: "none" } },
  };
  return (
    <div>
      <Tooltip target="[data-pr-tooltip]" position="top" pt={tooltipStyle} />
      <Dialog
        header={dialogHeader}
        visible={showModal}
        onHide={() => setShowModal(false)}
        style={{ width: dialogWidth, height: dialogHeight, maxWidth: "1400px" }}
        contentStyle={{ overflowY: "auto" }}
      >
        <LinkedinRequestsForm mode={formMode} initialData={selectedRequest} id={selectedRequest?.id} onSuccess={() => setShowModal(false)} />
      </Dialog>

      <DataTable
        value={linkedinrequest?.data ?? []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={linkedinrequest?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderLinkedinRequestsGrid />}
        rowsPerPageOptions={[2, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={["position", "city", "country", "jobType", "experienceLevel", "regimeType", "rangePosted", "isRecurring", "nextExecution", "fineshedExecution","status", "createdBy", "createdAt", "modifiedAt"]}
      >
        <Column
          field="position"
          header="Perfil"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="city"
          header="Cidade"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="country"
          header="País"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="isRecurring"
          body={(rowData) => (rowData.isRecurring ? "Sim" : "Não")}
          header="Recorrente?"
          sortable
          filter
          filterElement={(options) => (
              <TypeLinkedinRequestsDropdown options={options} inputType="recurring" />
            )}
          showFilterMatchModes={false}
        />
        <Column
          field="nextExecution"
          body={(rowData) =>
            rowData.status === "Concluído"
              ? "Finalizado"
              : formatDateSafe(rowData.nextExecution)
          }
          header="Próxima Execução"
          sortable
          filter
          showFilterMatchModes={false}
          filterElement={(options) => (
            <Calendar
              value={options.value ? new Date(options.value) : null}
              onChange={(e) =>
                options.filterApplyCallback(
                  e.value ? e.value.toISOString() : null
                )}
              dateFormat="dd-mm-yy"
              showIcon
              className="w-full"
              placeholder="Selecionar data"
              showButtonBar
            />)}
        />
        <Column
          field="fineshedExecution"
          body={(rowData) =>
            !rowData.isRecurring
              ? "Não aplicável"
              : rowData.fineshedExecution
                ? formatDateSafe(rowData.fineshedExecution)
                : "Indeterminado"
          }
          header="Finalização da Pesquisa"
          sortable
          filter
          showFilterMatchModes={false}
          filterElement={(options) => (
            <Calendar
              value={options.value ? new Date(options.value) : null}
              onChange={(e) =>
                options.filterApplyCallback(
                  e.value ? e.value.toISOString() : null
                )}
              dateFormat="dd-mm-yy"
              showIcon
              className="w-full"
              placeholder="Selecionar data"
              showButtonBar
            />)}
        />
        <Column
          field="status"
          header="Estado"
          sortable
          filter
          filterElement={(options) => (
            <TypeLinkedinRequestsDropdown options={options} inputType="status" />
          )}
          showFilterMatchModes={false}
        />
        <Column
          body={showActionOptions}
          header="Ações"
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Toast ref={toast} position="bottom-right" />
      <ConfirmDialog />
    </div>
  );
};

export default LinkedinRequestsGrid;
